import React, { useRef } from 'react'
import classNames from 'classnames'
import { isAndroid, isIOS } from 'react-device-detect'
import { CaptionXS } from '@/atoms/Text'
import { useTranslate } from '@/utils/translate/translate-client'

export interface DateInputProps {
  label: string
  value: string
  onChange: (value: string) => void
  onBlur?: () => void
  required?: boolean
  min?: string
  max?: string
  name?: string
  disabled?: boolean
  error?: string
  /** When true, disables the native date picker on iOS/Android, allowing manual text entry instead */
  disableNativePickerOnMobile?: boolean
}

/**
 * Auto-formats date input as YYYY-MM-DD while typing.
 * Inserts dashes immediately after year (4 digits) and month (6 digits).
 */
function formatDateInput(input: string, previousValue: string): string {
  // Remove all non-digit characters to get raw numbers
  const digits = input.replace(/\D/g, '')

  // Limit to 8 digits (YYYYMMDD)
  const limitedDigits = digits.slice(0, 8)

  // Build formatted string with dashes
  let formatted = ''
  for (let i = 0; i < limitedDigits.length; i++) {
    if (i === 4 || i === 6) {
      formatted += '-'
    }
    formatted += limitedDigits[i]
  }

  // Auto-append dash after year (4 digits) or month (6 digits) when typing forward
  const isTypingForward = input.length > previousValue.length
  if (isTypingForward && (limitedDigits.length === 4 || limitedDigits.length === 6)) {
    formatted += '-'
  }

  return formatted
}

export const DateInput: React.FC<DateInputProps> = ({
  label,
  value,
  onChange,
  onBlur,
  required = false,
  min,
  max,
  name,
  disabled = false,
  error,
  disableNativePickerOnMobile = false,
}) => {
  const { t } = useTranslate('common')
  const inputRef = useRef<HTMLInputElement>(null)

  const isMobileOS = isAndroid || isIOS
  const useTextInput = disableNativePickerOnMobile && isMobileOS

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (useTextInput) {
      const formatted = formatDateInput(e.target.value, value)
      onChange(formatted)
    } else {
      onChange(e.target.value)
    }
  }

  return (
    <div>
      {name && label && (
        <label htmlFor={name} className="photon-label-sm pb-1.5 font-medium">
          {label}
          {required ? '*' : ''}
        </label>
      )}
      <div className="relative mt-1">
        <input
          ref={inputRef}
          type={useTextInput ? 'text' : 'date'}
          inputMode={useTextInput ? 'numeric' : undefined}
          id={name}
          name={name}
          value={value}
          onChange={handleChange}
          onBlur={onBlur}
          required={required}
          min={useTextInput ? undefined : min}
          max={useTextInput ? undefined : max}
          maxLength={useTextInput ? 10 : undefined}
          disabled={disabled}
          className={classNames(
            'photon-paragraph-md w-full rounded-lg border p-2 !duration-[0ms] focus-visible:ring-0 focus-visible:ring-transparent focus-visible:ring-offset-0 focus-visible:ring-offset-transparent disabled:bg-white',
            error ? '!border-error-500' : 'border-core-gray-300',
          )}
          placeholder={useTextInput ? 'YYYY-MM-DD' : t('monthFormat', 'mm/dd/yyyy')}
          aria-invalid={!!error}
          aria-describedby={error ? `${name}-error` : undefined}
        />
      </div>
      <CaptionXS role="alert" className="min-h-[20px]">
        {error ? (
          <span id={`${name}-error`} className="text-error-500">
            {error}
          </span>
        ) : (
          <span>&nbsp;</span>
        )}
      </CaptionXS>
    </div>
  )
}
